import React, { ComponentType, LazyExoticComponent, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import ErrorBoundary from './ErrorBoundary';

// Loading component
const LazyLoadingSpinner = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
    <CircularProgress />
  </Box>
);

// Retry wrapper for lazy components with enhanced error handling
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  maxRetries: number = 3,
  fallbackComponent?: ComponentType<any>
): LazyExoticComponent<T> {
  return React.lazy(() => {
    return new Promise<{ default: T }>((resolve, reject) => {
      const load = (attempt: number = 1): void => {
        factory()
          .then(resolve)
          .catch((error) => {
            console.warn(`Lazy loading attempt ${attempt} failed:`, error);

            // Check if it's a chunk loading error
            const isChunkError = error.message?.includes('Loading chunk') ||
                               error.message?.includes('Failed to fetch dynamically imported module') ||
                               error.name === 'ChunkLoadError';

            if (attempt < maxRetries && isChunkError) {
              // For chunk errors, try to reload the page after max retries
              if (attempt === maxRetries) {
                console.error('Chunk loading failed multiple times, reloading page...');
                window.location.reload();
                return;
              }

              // Exponential backoff: 500ms, 1000ms, 1500ms
              const delay = attempt * 500;
              console.log(`Retrying chunk load in ${delay}ms...`);
              setTimeout(() => load(attempt + 1), delay);
            } else if (attempt < maxRetries) {
              // Regular retry for other errors
              const delay = attempt * 500;
              console.log(`Retrying in ${delay}ms...`);
              setTimeout(() => load(attempt + 1), delay);
            } else {
              console.error(`Failed to load after ${maxRetries} attempts`, error);

              // If we have a fallback component, resolve with it
              if (fallbackComponent) {
                resolve({ default: fallbackComponent as T });
              } else {
                reject(error);
              }
            }
          });
      };

      load();
    });
  });
}

// Wrapper component that provides error boundary and loading state
interface LazyWrapperProps {
  children: React.ReactNode;
}

export const LazyWrapper: React.FC<LazyWrapperProps> = ({ children }) => (
  <ErrorBoundary>
    <Suspense fallback={<LazyLoadingSpinner />}>
      {children}
    </Suspense>
  </ErrorBoundary>
);

export default LazyWrapper;